/**
 * Tool Detection Utility
 * Detects programming languages, frameworks, build tools, and runtime environments
 * from various sources (source code, package files, docker images, etc.)
 */

/**
 * Language patterns for detection from source code
 */
const LANGUAGE_PATTERNS = {
  javascript: {
    extensions: ['.js', '.mjs', '.cjs'],
    files: ['package.json', 'node_modules'],
    patterns: [/import .+ from/, /require\(/, /module\.exports/, /export default/]
  },
  typescript: {
    extensions: ['.ts', '.tsx'],
    files: ['tsconfig.json', 'package.json'],
    patterns: [/import .+ from/, /interface /, /type /, /: string|: number/]
  },
  python: {
    extensions: ['.py', '.pyw', '.pyx'],
    files: ['requirements.txt', 'setup.py', 'pyproject.toml', 'Pipfile'],
    patterns: [/import /, /from .+ import/, /def /, /class /]
  },
  java: {
    extensions: ['.java'],
    files: ['pom.xml', 'build.gradle', 'build.gradle.kts'],
    patterns: [/public class/, /package /, /import java\./, /@Override/]
  },
  csharp: {
    extensions: ['.cs', '.csx'],
    files: ['.csproj', '.sln', 'nuget.config'],
    patterns: [/namespace /, /using /, /public class/, /static void Main/]
  },
  go: {
    extensions: ['.go'],
    files: ['go.mod', 'go.sum'],
    patterns: [/package main/, /import \(/, /func /, /type .+ struct/]
  },
  rust: {
    extensions: ['.rs'],
    files: ['Cargo.toml', 'Cargo.lock'],
    patterns: [/fn main/, /use /, /pub fn/, /impl /]
  },
  php: {
    extensions: ['.php'],
    files: ['composer.json', 'composer.lock'],
    patterns: [/<\?php/, /namespace /, /use /, /function /]
  },
  kotlin: {
    extensions: ['.kt', '.kts'],
    files: ['build.gradle.kts', 'pom.xml'],
    patterns: [/fun main/, /package /, /import /, /class /]
  },
  cpp: {
    extensions: ['.cpp', '.cc', '.cxx', '.hpp', '.h'],
    files: ['CMakeLists.txt', 'Makefile'],
    patterns: [/#include/, /int main\(/, /namespace /, /class /]
  },
  c: {
    extensions: ['.c', '.h'],
    files: ['Makefile'],
    patterns: [/#include/, /int main\(/, /void /, /struct /]
  },
  ruby: {
    extensions: ['.rb', '.rake'],
    files: ['Gemfile', 'Rakefile'],
    patterns: [/require /, /def /, /class /, /module /]
  },
  elixir: {
    extensions: ['.ex', '.exs'],
    files: ['mix.exs'],
    patterns: [/defmodule /, /def /, /use /, /import /]
  },
  qsharp: {
    extensions: ['.qs'],
    files: ['qsharp.json'],
    patterns: [/namespace /, /operation /, /function /, /@EntryPoint/]
  },
  shell: {
    extensions: ['.sh', '.bash', '.zsh'],
    files: [],
    patterns: [/^#!\/bin\/(ba)?sh/, /export /, /function /]
  },
  yaml: {
    extensions: ['.yml', '.yaml'],
    files: [],
    patterns: [/^[a-zA-Z_]+:/, /^\s+-\s/]
  },
  json: {
    extensions: ['.json'],
    files: [],
    patterns: [/^{/, /^\[/, /"\w+":/]
  },
  html: {
    extensions: ['.html', '.htm'],
    files: [],
    patterns: [/<!DOCTYPE html>/, /<html/, /<head>/, /<body>/]
  },
  css: {
    extensions: ['.css', '.scss', '.sass', '.less'],
    files: [],
    patterns: [/\{[\s\S]*\}/, /[.#][a-zA-Z][\w-]*\s*{/]
  },
  sql: {
    extensions: ['.sql'],
    files: [],
    patterns: [/SELECT .* FROM/, /CREATE TABLE/, /INSERT INTO/, /UPDATE .* SET/]
  }
};

/**
 * Framework patterns for detection
 */
const FRAMEWORK_PATTERNS = {
  // Frontend Frameworks
  react: {
    files: ['package.json'],
    dependencies: ['react', 'react-dom'],
    patterns: [/import React/, /from ['"]react['"]/, /jsx/, /<[A-Z]\w+/],
    type: 'frontend'
  },
  nextjs: {
    files: ['package.json', 'next.config.js', 'next.config.mjs'],
    dependencies: ['next'],
    patterns: [/from ['"]next/, /export default function/, /getServerSideProps/, /getStaticProps/],
    type: 'frontend'
  },
  vue: {
    files: ['package.json', 'vue.config.js'],
    dependencies: ['vue'],
    patterns: [/<template>/, /<script>/, /export default \{/, /Vue\.component/],
    type: 'frontend'
  },
  angular: {
    files: ['package.json', 'angular.json'],
    dependencies: ['@angular/core'],
    patterns: [/@Component/, /@NgModule/, /from '@angular\//, /templateUrl:/],
    type: 'frontend'
  },
  svelte: {
    files: ['package.json', 'svelte.config.js'],
    dependencies: ['svelte'],
    patterns: [/<script>/, /<style>/, /export let/],
    type: 'frontend'
  },
  reactnative: {
    files: ['package.json', 'app.json'],
    dependencies: ['react-native'],
    patterns: [/from ['"]react-native['"]/, /AppRegistry/, /StyleSheet\.create/],
    type: 'mobile'
  },
  jquery: {
    files: ['package.json'],
    dependencies: ['jquery'],
    patterns: [/\$\(/, /jQuery\(/, /\.ajax\(/],
    type: 'frontend'
  },
  
  // Backend Frameworks
  express: {
    files: ['package.json'],
    dependencies: ['express'],
    patterns: [/require\(['"]express['"]\)/, /app\.get\(/, /app\.post\(/],
    type: 'backend'
  },
  fastify: {
    files: ['package.json'],
    dependencies: ['fastify'],
    patterns: [/require\(['"]fastify['"]\)/, /fastify\.get\(/, /fastify\.post\(/],
    type: 'backend'
  },
  nestjs: {
    files: ['package.json', 'nest-cli.json'],
    dependencies: ['@nestjs/core'],
    patterns: [/@Controller/, /@Injectable/, /@Module/, /from '@nestjs\//],
    type: 'backend'
  },
  django: {
    files: ['requirements.txt', 'manage.py', 'settings.py'],
    dependencies: ['django'],
    patterns: [/from django/, /django\./, /INSTALLED_APPS/, /urlpatterns/],
    type: 'backend'
  },
  flask: {
    files: ['requirements.txt'],
    dependencies: ['flask', 'Flask'],
    patterns: [/from flask import/, /Flask\(__name__\)/, /@app\.route/],
    type: 'backend'
  },
  fastapi: {
    files: ['requirements.txt', 'pyproject.toml'],
    dependencies: ['fastapi'],
    patterns: [/from fastapi import/, /FastAPI\(\)/, /@app\.get/, /@app\.post/],
    type: 'backend'
  },
  springboot: {
    files: ['pom.xml', 'build.gradle'],
    dependencies: ['spring-boot-starter'],
    patterns: [/@SpringBootApplication/, /@RestController/, /@GetMapping/],
    type: 'backend'
  },
  aspnet: {
    files: ['.csproj', 'Program.cs', 'Startup.cs'],
    dependencies: ['Microsoft.AspNetCore'],
    patterns: [/using Microsoft\.AspNetCore/, /WebApplication\.CreateBuilder/, /app\.Run\(/],
    type: 'backend'
  },
  gin: {
    files: ['go.mod'],
    dependencies: ['github.com/gin-gonic/gin'],
    patterns: [/gin\.Default\(\)/, /router\.GET\(/, /gin\.Context/],
    type: 'backend'
  },
  actix: {
    files: ['Cargo.toml'],
    dependencies: ['actix-web'],
    patterns: [/use actix_web/, /HttpServer::new/, /#\[get\(/, /#\[post\(/],
    type: 'backend'
  }
};

/**
 * Build tool patterns
 */
const BUILD_TOOLS = {
  npm: {
    files: ['package.json', 'package-lock.json'],
    commands: ['npm']
  },
  yarn: {
    files: ['yarn.lock'],
    commands: ['yarn']
  },
  pnpm: {
    files: ['pnpm-lock.yaml'],
    commands: ['pnpm']
  },
  maven: {
    files: ['pom.xml'],
    commands: ['mvn']
  },
  gradle: {
    files: ['build.gradle', 'build.gradle.kts', 'gradlew'],
    commands: ['gradle', './gradlew']
  },
  cargo: {
    files: ['Cargo.toml', 'Cargo.lock'],
    commands: ['cargo']
  },
  pip: {
    files: ['requirements.txt', 'setup.py'],
    commands: ['pip']
  },
  poetry: {
    files: ['pyproject.toml', 'poetry.lock'],
    commands: ['poetry']
  },
  composer: {
    files: ['composer.json', 'composer.lock'],
    commands: ['composer']
  },
  make: {
    files: ['Makefile'],
    commands: ['make']
  },
  cmake: {
    files: ['CMakeLists.txt'],
    commands: ['cmake']
  },
  webpack: {
    files: ['webpack.config.js'],
    commands: ['webpack']
  },
  vite: {
    files: ['vite.config.js', 'vite.config.ts'],
    commands: ['vite']
  },
  rollup: {
    files: ['rollup.config.js'],
    commands: ['rollup']
  },
  parcel: {
    files: ['.parcelrc'],
    commands: ['parcel']
  }
};

/**
 * Detect programming language from source code and file structure
 * @param {Object} sourceCode - Object with filenames as keys and content as values
 * @returns {Array<string>} Array of detected languages (sorted by confidence)
 */
export const detectLanguages = (sourceCode) => {
  if (!sourceCode || typeof sourceCode !== 'object') {
    return [];
  }

  const detectionScores = {};
  const filenames = Object.keys(sourceCode);

  // Check each language pattern
  Object.entries(LANGUAGE_PATTERNS).forEach(([lang, config]) => {
    let score = 0;

    // Check file extensions
    filenames.forEach(filename => {
      const ext = filename.substring(filename.lastIndexOf('.'));
      if (config.extensions.includes(ext)) {
        score += 10;
      }
    });

    // Check for specific marker files
    config.files.forEach(markerFile => {
      if (filenames.some(f => f.includes(markerFile))) {
        score += 15;
      }
    });

    // Check content patterns
    Object.values(sourceCode).forEach(content => {
      if (typeof content === 'string') {
        config.patterns.forEach(pattern => {
          if (pattern.test(content)) {
            score += 5;
          }
        });
      }
    });

    if (score > 0) {
      detectionScores[lang] = score;
    }
  });

  // Sort languages by score (highest first)
  return Object.entries(detectionScores)
    .sort((a, b) => b[1] - a[1])
    .map(([lang]) => lang);
};

/**
 * Detect frameworks from source code and dependencies
 * @param {Object} sourceCode - Object with filenames as keys and content as values
 * @returns {Object} Object with frontend, backend, and mobile frameworks
 */
export const detectFrameworks = (sourceCode) => {
  if (!sourceCode || typeof sourceCode !== 'object') {
    return { frontend: [], backend: [], mobile: [], all: [] };
  }

  const detectedFrameworks = {
    frontend: [],
    backend: [],
    mobile: [],
    all: []
  };

  const filenames = Object.keys(sourceCode);
  
  // Parse package.json if available for dependency checking
  let dependencies = {};
  const packageJsonFile = filenames.find(f => f.endsWith('package.json'));
  if (packageJsonFile) {
    try {
      const packageJson = JSON.parse(sourceCode[packageJsonFile]);
      dependencies = {
        ...packageJson.dependencies,
        ...packageJson.devDependencies
      };
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Check each framework pattern
  Object.entries(FRAMEWORK_PATTERNS).forEach(([framework, config]) => {
    let detected = false;

    // Check for marker files
    if (config.files.some(file => filenames.some(f => f.includes(file)))) {
      detected = true;
    }

    // Check dependencies
    if (dependencies && Object.keys(dependencies).length > 0) {
      if (config.dependencies.some(dep => dep in dependencies)) {
        detected = true;
      }
    }

    // Check content patterns
    if (!detected) {
      Object.values(sourceCode).forEach(content => {
        if (typeof content === 'string') {
          if (config.patterns.some(pattern => pattern.test(content))) {
            detected = true;
          }
        }
      });
    }

    if (detected) {
      detectedFrameworks.all.push(framework);
      if (config.type === 'frontend') {
        detectedFrameworks.frontend.push(framework);
      } else if (config.type === 'backend') {
        detectedFrameworks.backend.push(framework);
      } else if (config.type === 'mobile') {
        detectedFrameworks.mobile.push(framework);
      }
    }
  });

  return detectedFrameworks;
};

/**
 * Detect build tools from project files
 * @param {Object} sourceCode - Object with filenames as keys
 * @returns {Array<string>} Array of detected build tools
 */
export const detectBuildTools = (sourceCode) => {
  if (!sourceCode || typeof sourceCode !== 'object') {
    return [];
  }

  const detected = [];
  const filenames = Object.keys(sourceCode);

  Object.entries(BUILD_TOOLS).forEach(([tool, config]) => {
    if (config.files.some(file => filenames.some(f => f.includes(file)))) {
      detected.push(tool);
    }
  });

  return detected;
};

/**
 * Detect runtime environment from Docker image
 * @param {string} dockerImage - Docker image name
 * @returns {Object} Runtime information
 */
export const detectRuntimeFromDocker = (dockerImage) => {
  if (!dockerImage || typeof dockerImage !== 'string') {
    return { runtime: null, version: null };
  }

  const image = dockerImage.toLowerCase();
  
  // Node.js
  if (image.includes('node')) {
    const versionMatch = image.match(/node:(\d+)/);
    return {
      runtime: 'nodejs',
      version: versionMatch ? versionMatch[1] : null
    };
  }
  
  // Python
  if (image.includes('python')) {
    const versionMatch = image.match(/python:([\d.]+)/);
    return {
      runtime: 'python',
      version: versionMatch ? versionMatch[1] : null
    };
  }
  
  // Java
  if (image.includes('openjdk') || image.includes('java')) {
    const versionMatch = image.match(/(openjdk|java):(\d+)/);
    return {
      runtime: 'java',
      version: versionMatch ? versionMatch[2] : null
    };
  }
  
  // .NET
  if (image.includes('dotnet') || image.includes('aspnet')) {
    const versionMatch = image.match(/(dotnet|aspnet):([\d.]+)/);
    return {
      runtime: 'dotnet',
      version: versionMatch ? versionMatch[2] : null
    };
  }
  
  // Go
  if (image.includes('golang')) {
    const versionMatch = image.match(/golang:([\d.]+)/);
    return {
      runtime: 'go',
      version: versionMatch ? versionMatch[1] : null
    };
  }
  
  // Rust
  if (image.includes('rust')) {
    const versionMatch = image.match(/rust:([\d.]+)/);
    return {
      runtime: 'rust',
      version: versionMatch ? versionMatch[1] : null
    };
  }
  
  // PHP
  if (image.includes('php')) {
    const versionMatch = image.match(/php:([\d.]+)/);
    return {
      runtime: 'php',
      version: versionMatch ? versionMatch[1] : null
    };
  }
  
  // Nginx (for static sites)
  if (image.includes('nginx')) {
    return {
      runtime: 'nginx',
      version: null
    };
  }
  
  return { runtime: null, version: null };
};

/**
 * Comprehensive tool detection - analyzes all aspects
 * @param {Object} service - Service configuration from Mia Platform
 * @param {Object} sourceCode - Source code files
 * @returns {Object} Complete tool detection results
 */
export const detectAllTools = (service, sourceCode) => {
  const result = {
    languages: [],
    frameworks: { frontend: [], backend: [], mobile: [], all: [] },
    buildTools: [],
    runtime: null,
    containerInfo: null,
    git: null,
    database: null
  };

  // Detect from source code
  if (sourceCode) {
    result.languages = detectLanguages(sourceCode);
    result.frameworks = detectFrameworks(sourceCode);
    result.buildTools = detectBuildTools(sourceCode);
  }

  // Detect from Docker image
  if (service?.dockerImage) {
    result.runtime = detectRuntimeFromDocker(service.dockerImage);
    result.containerInfo = {
      image: service.dockerImage,
      ...result.runtime
    };
  }

  // Git provider detection
  if (service?.repositoryUrl || service?.repoUrl || service?.sshUrl) {
    const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
    result.git = {
      url: repoUrl,
      provider: repoUrl.includes('github.com') ? 'github' : 
                repoUrl.includes('gitlab.com') ? 'gitlab' : 
                repoUrl.includes('bitbucket.org') ? 'bitbucket' : 'unknown'
    };
  }

  // Database detection from environment variables
  if (service?.environment) {
    const envVars = service.environment;
    const dbTypes = [];
    
    envVars.forEach(env => {
      const key = env.key || env.name || '';
      const value = env.value || '';
      
      if (key.includes('MONGO') || value.includes('mongodb://')) {
        dbTypes.push('mongodb');
      }
      if (key.includes('POSTGRES') || value.includes('postgresql://')) {
        dbTypes.push('postgresql');
      }
      if (key.includes('MYSQL') || value.includes('mysql://')) {
        dbTypes.push('mysql');
      }
      if (key.includes('REDIS') || value.includes('redis://')) {
        dbTypes.push('redis');
      }
    });
    
    if (dbTypes.length > 0) {
      result.database = [...new Set(dbTypes)];
    }
  }

  return result;
};

/**
 * Get display name and icon for detected tools
 * @param {string} tool - Tool name
 * @param {string} type - Tool type (language, framework, build)
 * @returns {Object} Display information
 */
export const getToolDisplay = (tool, type = 'language') => {
  const displays = {
    // Languages
    javascript: { name: 'JavaScript', icon: '🟨', color: '#F7DF1E' },
    typescript: { name: 'TypeScript', icon: '🔷', color: '#3178C6' },
    python: { name: 'Python', icon: '🐍', color: '#3776AB' },
    java: { name: 'Java', icon: '☕', color: '#007396' },
    csharp: { name: 'C#', icon: '🟣', color: '#239120' },
    go: { name: 'Go', icon: '🔵', color: '#00ADD8' },
    rust: { name: 'Rust', icon: '🦀', color: '#CE412B' },
    php: { name: 'PHP', icon: '🐘', color: '#777BB4' },
    kotlin: { name: 'Kotlin', icon: '🟣', color: '#7F52FF' },
    cpp: { name: 'C++', icon: '⚙️', color: '#00599C' },
    ruby: { name: 'Ruby', icon: '💎', color: '#CC342D' },
    elixir: { name: 'Elixir', icon: '💜', color: '#4B275F' },
    qsharp: { name: 'Q#', icon: '⚛️', color: '#00BCF2' },
    
    // Frontend Frameworks
    react: { name: 'React', icon: '⚛️', color: '#61DAFB' },
    nextjs: { name: 'Next.js', icon: '▲', color: '#000000' },
    vue: { name: 'Vue.js', icon: '💚', color: '#4FC08D' },
    angular: { name: 'Angular', icon: '🅰️', color: '#DD0031' },
    svelte: { name: 'Svelte', icon: '🔥', color: '#FF3E00' },
    reactnative: { name: 'React Native', icon: '📱', color: '#61DAFB' },
    jquery: { name: 'jQuery', icon: '💙', color: '#0769AD' },
    
    // Backend Frameworks
    express: { name: 'Express', icon: '🚂', color: '#000000' },
    fastify: { name: 'Fastify', icon: '⚡', color: '#000000' },
    nestjs: { name: 'NestJS', icon: '🐱', color: '#E0234E' },
    django: { name: 'Django', icon: '🎸', color: '#092E20' },
    flask: { name: 'Flask', icon: '🌶️', color: '#000000' },
    fastapi: { name: 'FastAPI', icon: '⚡', color: '#009688' },
    springboot: { name: 'Spring Boot', icon: '🍃', color: '#6DB33F' },
    aspnet: { name: 'ASP.NET', icon: '🟣', color: '#512BD4' },
    gin: { name: 'Gin', icon: '🍸', color: '#00ADD8' },
    actix: { name: 'Actix', icon: '🦀', color: '#CE412B' },
    
    // Build Tools
    npm: { name: 'npm', icon: '📦', color: '#CB3837' },
    yarn: { name: 'Yarn', icon: '🧶', color: '#2C8EBB' },
    pnpm: { name: 'pnpm', icon: '📦', color: '#F69220' },
    maven: { name: 'Maven', icon: '🔨', color: '#C71A36' },
    gradle: { name: 'Gradle', icon: '🐘', color: '#02303A' },
    cargo: { name: 'Cargo', icon: '📦', color: '#CE412B' },
    webpack: { name: 'Webpack', icon: '📦', color: '#8DD6F9' },
    vite: { name: 'Vite', icon: '⚡', color: '#646CFF' }
  };

  return displays[tool?.toLowerCase()] || { name: tool, icon: '🔧', color: '#666666' };
};

export default {
  detectLanguages,
  detectFrameworks,
  detectBuildTools,
  detectRuntimeFromDocker,
  detectAllTools,
  getToolDisplay
};
